// src/utils/apiClient.js
import axios from "axios";
import { getBaseUrl } from "./getBaseUrl"; // Make sure this returns your base URL
import { isUnderBaseDomain } from "./brandHost";

const apiClient = axios.create({
  baseURL: getBaseUrl(),
  headers: {
    "Content-Type": "application/json",
  },
});

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // A file upload is multipart, and the browser writes that header with its
    // boundary. Left at the JSON default above, axios turns a FormData into
    // JSON and the file never reaches the server — every upload that did not
    // pass its own "multipart/form-data" arrived as `{}`.
    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
      config.headers.setContentType(undefined);
    }
    // For custom domains (lms.abcinstitute.com), the Origin header already
    // carries the domain. But for extra safety, if we're on a non-lmscampus
    // hostname, send it explicitly so the backend tenant resolver can use it.
    // Local dev: vc.localhost → send X-Institute-Subdomain: vc
    const hostname = window.location.hostname;
    if (hostname.endsWith(".localhost")) {
      const sub = hostname.slice(0, -(".localhost".length));
      if (sub) config.headers["X-Institute-Subdomain"] = sub;
    } else if (
      hostname !== "localhost" &&
      !isUnderBaseDomain(hostname)
    ) {
      config.headers["X-Custom-Domain"] = hostname;
    }

    // Attach selected branch (if any) for branch-scoped queries
    const branchId = localStorage.getItem('vc-selected-branch');
    if (branchId && branchId !== 'all') {
      config.headers["X-Branch-Id"] = branchId;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => {
    // ── Grace period detection from response headers ──
    // The backend sets X-Plan-Grace headers when trial/subscription expired
    // but the institute is still within the 7-day grace window.
    const isGrace = response.headers?.['x-plan-grace'] === 'true';
    if (isGrace) {
      const graceDays = parseInt(response.headers['x-plan-grace-days'] || '0', 10);
      const graceStatus = response.headers['x-plan-status'] || 'expired';
      // Dispatch a custom event so PlanContext / dashboard can pick it up
      window.dispatchEvent(new CustomEvent('vc-plan-grace', {
        detail: { inGrace: true, daysLeft: graceDays, status: graceStatus },
      }));
    }
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const code = error.response?.data?.code;

    if (status === 401) {
      // If no token was stored, this is an EXPECTED 401 from a background
      // call on a public page (e.g. CMS public site, /login). It is NOT a
      // session expiry — do not wipe storage or force-redirect.
      const hadToken = !!localStorage.getItem('token');
      if (!hadToken) {
        return Promise.reject(error);
      }

      const isSessionReplaced = code === 'SESSION_REPLACED';
      const isSessionRevoked = code === 'SESSION_REVOKED';

      // ── Platform support session ended underneath this tab ──
      // It was superseded by a newer one, revoked from the console, or hit its
      // hard expiry. Tearing down the support state is NOT optional here: the
      // generic path below drops only token/user, and a leftover
      // `vc-impersonation` key would then paint a support banner over the next
      // ordinary login by the actual customer on this browser.
      const impRaw = localStorage.getItem('vc-impersonation');
      if (impRaw) {
        let returnUrl = null;
        try { returnUrl = JSON.parse(impRaw)?.returnUrl || null; } catch { /* malformed */ }
        localStorage.removeItem('vc-impersonation');
        localStorage.removeItem('vc-impersonation-prev');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('selectedRole');
        localStorage.removeItem('vc-selected-branch');
        window.dispatchEvent(new Event('vc-auth-change'));
        // Back to the operator console, never to a login page on the
        // customer's domain where the only way forward is credentials the
        // operator does not have.
        window.location.href = returnUrl || '/login';
        return Promise.reject(error);
      }

      // ── An institute opened from a distributor's dashboard, ended ──
      // Expired, signed out elsewhere, or access withdrawn. Its owner account
      // has no password, so this institute's login page is a dead end — send
      // the person back to the dashboard they came from.
      const partnerRaw = localStorage.getItem('vc-partner-session');
      if (partnerRaw) {
        let returnUrl = null;
        try { returnUrl = JSON.parse(partnerRaw)?.returnUrl || null; } catch { /* malformed */ }
        localStorage.removeItem('vc-partner-session');
        localStorage.removeItem('vc-partner-session-prev');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('selectedRole');
        localStorage.removeItem('vc-selected-branch');
        window.dispatchEvent(new Event('vc-auth-change'));
        window.location.href = returnUrl || '/login';
        return Promise.reject(error);
      }

      // Token expired or invalid — clear auth & redirect
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('selectedRole');
      window.dispatchEvent(new Event('vc-auth-change'));
      if (isSessionReplaced) {
        sessionStorage.setItem('logoutReason', error.response?.data?.message || 'You have been logged out because you logged in from another device or tab.');
      } else if (isSessionRevoked) {
        sessionStorage.setItem('logoutReason', error.response?.data?.message || 'This session was signed out from another device.');
      }
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    }

    // ── Plan / Trial expired (grace period over) ──
    if (status === 403 && (code === 'TRIAL_EXPIRED' || code === 'PLAN_EXPIRED')) {
      // Dispatch event so the app can show a full-screen upgrade prompt
      window.dispatchEvent(new CustomEvent('vc-plan-expired', {
        detail: { code, message: error.response?.data?.message },
      }));
      // Redirect to billing page if not already there
      if (!window.location.pathname.startsWith('/billing')) {
        window.location.href = '/billing';
      }
    }

    // ── Read-only platform support session ──
    // A write was attempted inside an impersonated session that was started in
    // read-only mode. Without this the UI just shows a bare "Access denied",
    // which reads like a permissions bug in the customer's account — the exact
    // wrong conclusion when you are here to diagnose one.
    if (status === 403 && code === 'IMPERSONATION_READ_ONLY') {
      window.dispatchEvent(new CustomEvent('vc-impersonation-readonly', {
        detail: { message: error.response?.data?.message },
      }));
    }

    // ── An action the admin's permissions do not allow ──
    // Only for something the person DID (not a background read), and named,
    // so the toast can say what to ask for (Common/PermissionDeniedToast).
    if (status === 403 && code === 'PERMISSION_REQUIRED' && (error.config?.method || 'get').toLowerCase() !== 'get') {
      window.dispatchEvent(new CustomEvent('vc-permission-denied', {
        detail: { labels: error.response?.data?.requiredLabels || [] },
      }));
    }

    // ── Institute suspended ──
    if (status === 403 && code === 'INSTITUTE_SUSPENDED') {
      window.dispatchEvent(new CustomEvent('vc-institute-suspended', {
        detail: { message: error.response?.data?.message },
      }));
    }

    // ── Mandatory training overdue (compliance blocking gate) ──
    if (status === 403 && code === 'MANDATORY_BLOCKED') {
      window.dispatchEvent(new CustomEvent('vc-mandatory-blocked', {
        detail: {
          message: error.response?.data?.message,
          redirectTo: error.response?.data?.redirectTo || '/s/compliance',
        },
      }));
    }

    // ── Plan limit or no-plan reached (planGate middleware) ──
    // Only show toast for numeric limits (limitField present), NOT for
    // feature-toggle rejections (featureField present) — those are handled
    // silently by PlanGate / LockedFeaturePage.
    const data = error.response?.data || {};
    if (status === 403 && (data.planLimit || data.noPlan) && !data.featureField) {
      window.dispatchEvent(new CustomEvent('vc-plan-limit', {
        detail: {
          message: data.message || 'You have reached a plan limit.',
          limitField: data.limitField,
          current: data.current,
          max: data.max,
          noPlan: !!data.noPlan,
        },
      }));
    }

    return Promise.reject(error);
  }
);

export default apiClient;
